import { isAdmin, type Provider } from '~/entities/provider'
import type { FC } from 'react'
import { Link } from 'react-router'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import IconButton from '@mui/material/IconButton'
import SettingsIcon from '@mui/icons-material/Settings'
import { Box, Typography } from '@mui/material'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'

import type { MeState } from '~/app/store/reducers/me'
import { Loader } from '~/shared/ui'
import logo from '~/assets/images/logo.png'
import { useLogoutEffects } from '~/app/auth'
import { SERVICE_VERSION } from '~/shared/lib/config'
import { PeopleSearch, PersonInstantNav } from '~/features/member-search'
import { Tooltip } from '@mui/material'

interface InterfaceHeaderProps {
  synchronizing: boolean
  me: MeState
  providers: Provider[]
}

const InterfaceHeader: FC<InterfaceHeaderProps> = props => {
  const { me, providers, synchronizing } = props
  const { run: handleLogout } = useLogoutEffects({
    auth0: { logoutParams: { returnTo: window.location.origin } },
  })

  const renderSync = () => {
    return synchronizing ? (
      <Tooltip placement="left" title="Synchronizing...">
        <Typography component="div" sx={{ color: 'primary.main' }}>
          <Box sx={{ mr: 2 }}>
            <Loader inline />
          </Box>
        </Typography>
      </Tooltip>
    ) : null
  }

  if (!me?.provider_fields) return null

  // Find if user is in admin group
  const orderedStates = me.provider_fields.practicing_states
    ? me.provider_fields.practicing_states.sort((a, b) => a.name.localeCompare(b.name))
    : []
  let defaultState = 'All'
  if (!isAdmin(providers, me.id) && orderedStates.length > 0) {
    defaultState = orderedStates.map(location => location.abbreviation).join(',')
  }
  let currentAdminName
  if (me) {
    currentAdminName = me.first_name + ' ' + me.last_name
  }

  return (
    <Box
      sx={{
        height: 64,
        borderBottom: '0.1rem solid',
        borderColor: 'grey.100',
        boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.05)',
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
        <Tooltip placement="right" enterDelay={1000} title={`Version: ${SERVICE_VERSION ?? ''}`}>
          <Link
            to="/inbox"
            style={{
              display: 'block',
              width: 100,
              height: 24,
              marginRight: 24,
              background: `url(${logo}) center no-repeat`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
            }}
          />
        </Tooltip>

        <PeopleSearch />

        <Box sx={{ ml: 1 }}>
          <PersonInstantNav />
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            marginLeft: 2,
            color: 'navy.300',
            fontSize: '1.3rem',
          }}
        >
          <LocationOnOutlinedIcon fontSize="small" />
          {defaultState}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
        {renderSync()}

        <Box sx={{ display: 'flex', flexDirection: 'column', mr: 2, color: 'text.secondary' }}>
          <Typography sx={{ color: 'text.primary' }}>{currentAdminName}</Typography>
        </Box>

        <Box sx={{ color: 'grey.400' }}>
          {me && !me.provider_fields?.is_external ? (
            <Link to="/settings">
              <Tooltip title="Settings">
                <IconButton color="inherit">
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Link>
          ) : null}

          <Tooltip title="Log out">
            <IconButton onClick={handleLogout} color="inherit" aria-label="Log out">
              <ExitToAppIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  )
}

export default InterfaceHeader
